import { useState } from 'react';
import { DocsIcon } from '@backstage/core-components';
import { IconButton, Tooltip } from '@material-ui/core';
import CardActions from '@mui/material/CardActions';
import { BaseEditIcon } from '../BaseEditIcon';
import { KmsCardEditDialog } from './KmsCardEditDialog';
import { kms } from './KmsCard';
import _ from 'lodash';

interface KmsCardEditIconProps {
  workloadData: any;
  requesterEmail: string;
  isEngineer: () => boolean;
  workloadFeature: {
    name: string;
    docs: string;
  };
  accountName: [];
  callPatchApi: (kms: {}) => Promise<any>;
  _kms: kms | undefined;
}

export const KmsCardEditIcon = ({
  workloadData,
  requesterEmail,
  isEngineer,
  workloadFeature,
  accountName,
  callPatchApi,
  _kms,
}: KmsCardEditIconProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [kmsEnabled, setKmsEnabled] = useState(_kms?.attribute_key_enabled);
  const [standardAccountList, setStandardAccountList] = useState<
    any[] | undefined
  >(_kms?.standard_key_share_accounts);
  const [attributeAccountList, setAttributeAccountList] = useState<
    any[] | undefined
  >(_kms?.attribute_key_share_accounts);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const handleClickOpen = () => {
    // Reset state to current values when opening
    setKmsEnabled(_kms?.attribute_key_enabled);
    setStandardAccountList(_kms?.standard_key_share_accounts);
    setAttributeAccountList(_kms?.attribute_key_share_accounts);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  // Handle KMS toggle with confirmation
  const handleKmsToggle = () => {
    if (kmsEnabled) {
      setIsConfirmDialogOpen(true);
    } else {
      setKmsEnabled(true);
    }
  };

  const handleConfirmDialogNo = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleConfirmDialogYes = () => {
    setIsConfirmDialogOpen(false);
    setKmsEnabled(false);
    setAttributeAccountList([]);
  };

  const handleConfirm = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    const currentConfig = {
      attribute_key_enabled: _kms?.attribute_key_enabled,
      attribute_key_share_accounts: _kms?.attribute_key_share_accounts || [],
      standard_key_share_accounts: _kms?.standard_key_share_accounts || [],
    };

    const updatedConfig = {
      attribute_key_enabled: kmsEnabled,
      attribute_key_share_accounts: attributeAccountList || [],
      standard_key_share_accounts: standardAccountList || [],
    };

    // Patch when there's an actual change
    if (!_.isEqual(currentConfig, updatedConfig)) {
      setIsLoading(true);
      await callPatchApi(updatedConfig);
      setIsLoading(false);
      handleClose();
    }
  };

  return (
    <CardActions>
      <Tooltip title={workloadFeature.name}>
        <IconButton href={workloadFeature.docs} target="_blank">
          <DocsIcon />
        </IconButton>
      </Tooltip>
      <BaseEditIcon
        workloadData={workloadData}
        requesterEmail={requesterEmail}
        handleClickOpen={handleClickOpen}
        isModalOpen={isModalOpen}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        disableConfirm={!isEngineer()}
        apiValidationPayload={{}}
        isLoading={isLoading}
        isLoadingEdit={false}
        dialogTitle="Update Foundation Managed KMS"
        allowEngineerEdit
      >
        <KmsCardEditDialog
          accountName={accountName}
          kmsEnabled={kmsEnabled}
          setKmsEnabled={setKmsEnabled}
          standardAccountList={standardAccountList}
          setStandardAccountList={setStandardAccountList}
          attributeAccountList={attributeAccountList}
          setAttributeAccountList={setAttributeAccountList}
          handleKmsToggle={handleKmsToggle}
          isConfirmDialogOpen={isConfirmDialogOpen}
          handleConfirmDialogNo={handleConfirmDialogNo}
          handleConfirmDialogYes={handleConfirmDialogYes}
        />
      </BaseEditIcon>
    </CardActions>
  );
};
